import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import searchRecipientsQuery from './searchRecipients.graphql';
import sendBroadcastMutation from './sendBroadcast.graphql';

// Admin Messaging — compose a custom message and send it (Email and/or Push) to a group (all clients /
// all workers / all users) or to a hand-picked list of individuals (searched by name / phone / email).
function Messaging() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [email, setEmail] = useState(true);
  const [push, setPush] = useState(true);
  const [audience, setAudience] = useState('allClients'); // allClients | allWorkers | allUsers | custom
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [result, setResult] = useState(null);

  const [runSearch, { data: searchData, loading: searching }] = useLazyQuery(searchRecipientsQuery, {
    fetchPolicy: 'network-only',
  });
  const [runSend, { loading: sending }] = useMutation(sendBroadcastMutation);

  const found = (searchData && searchData.searchRecipients && searchData.searchRecipients.results) || [];

  const add = (r) => { if (!picked.find((p) => p.id === r.id)) setPicked([...picked, r]); };
  const remove = (id) => setPicked(picked.filter((p) => p.id !== id));

  const doSearch = () => {
    if ((query || '').trim().length >= 2) runSearch({ variables: { query: query.trim() } });
  };

  const send = async () => {
    setResult(null);
    if (!subject.trim() || !body.trim()) { setResult({ error: 'Subject and message are required.' }); return; }
    if (!email && !push) { setResult({ error: 'Pick at least one channel (Email or Push).' }); return; }
    const isCustom = audience === 'custom';
    if (isCustom && !picked.length) { setResult({ error: 'Add at least one recipient.' }); return; }
    const channels = [...(email ? ['email'] : []), ...(push ? ['push'] : [])];
    try {
      const res = await runSend({
        variables: {
          subject, body, channels,
          audience: isCustom ? 'custom' : audience,
          userIds: isCustom ? picked.map((p) => p.id) : [],
        },
      });
      const r = res && res.data && res.data.sendBroadcast;
      if (r && r.status === 200) {
        setResult({ ok: `Sent to ${r.total} recipient(s) — ${r.sentEmail} email, ${r.sentPush} push.` });
      } else {
        setResult({ error: (r && r.errorMessage) || 'Send failed.' });
      }
    } catch (e) {
      setResult({ error: 'Network error. Please try again.' });
    }
  };

  const input = { width: '100%', padding: '10px 12px', border: '1px solid #d6d9de', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const label = { fontWeight: 600, fontSize: 13, color: '#333', margin: '14px 0 6px' };
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', color: '#3730a3', borderRadius: 16, padding: '5px 10px', fontSize: 13, margin: '0 6px 6px 0' };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Messaging</h2>
      <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 18 }}>
        Send a message by Email and/or Push to a group or to specific people.
      </div>

      <div style={label}>Subject</div>
      <input style={input} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />

      <div style={label}>Message</div>
      <textarea style={{ ...input, minHeight: 120, resize: 'vertical' }} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" />

      <div style={label}>Channels</div>
      <label style={{ marginRight: 18, fontSize: 14 }}>
        <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Email
      </label>
      <label style={{ fontSize: 14 }}>
        <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} /> Push notification
      </label>

      <div style={label}>Recipients</div>
      <select style={input} value={audience} onChange={(e) => setAudience(e.target.value)}>
        <option value="allClients">All clients</option>
        <option value="allWorkers">All workers</option>
        <option value="allUsers">All users</option>
        <option value="custom">Specific people…</option>
      </select>

      {audience === 'custom' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doSearch(); } }}
              placeholder="Search by name, phone, or email…"
            />
            <button onClick={doSearch} disabled={searching} style={{ padding: '0 16px', borderRadius: 8, border: '1px solid #d6d9de', background: '#fff', cursor: 'pointer' }}>
              {searching ? '…' : 'Search'}
            </button>
          </div>

          {found.length > 0 && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
              {found.map((r) => (
                <div key={r.id} onClick={() => add(r)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
                  <b>{r.name || '(no name)'}</b> · {r.phoneNumber || '—'} · {r.email || '—'}
                  <span style={{ color: '#9ca3af' }}> · {r.userType === 2 ? 'worker' : 'client'}</span>
                </div>
              ))}
            </div>
          )}

          {picked.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {picked.map((p) => (
                <span key={p.id} style={chip}>
                  {p.name || p.phoneNumber || p.email}
                  <span onClick={() => remove(p.id)} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 22 }}>
        <button
          onClick={send}
          disabled={sending}
          style={{ background: '#E8232B', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: sending ? 0.6 : 1 }}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, fontSize: 14, background: result.ok ? '#ecfdf5' : '#fef2f2', color: result.ok ? '#065f46' : '#991b1b' }}>
          {result.ok || result.error}
        </div>
      )}
    </div>
  );
}

export default Messaging;
